// ./src/components/features/UserInvitations/UserInvitationsTable/UserInvitationsTable.tsx
import React, { useMemo } from "react";
import { Group, Text } from "@mantine/core";

import { UserInvitation } from "@/utils/api";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  AppButton,
} from "@/components/generic";

export interface UserInvitationsTableProps {
  /** Array of user invitations to display */
  userInvitations: UserInvitation[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditInvitation?: (invitation: UserInvitation) => void;

  /** Callback when remove action is clicked */
  onRemoveInvitation?: (invitation: UserInvitation) => void;

  /** Callback when resend action is clicked */
  onResendInvitation?: (invitation: UserInvitation) => void;

  /** ID of invitation currently being removed */
  removingInvitationId?: string | null;

  /** ID of invitation currently being resent */
  resendingInvitationId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const UserInvitationsTable: React.FC<UserInvitationsTableProps> = ({
  userInvitations,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditInvitation,
  onRemoveInvitation,
  onResendInvitation,
  removingInvitationId,
  resendingInvitationId,
  size = "md",
}) => {
  // Helper to check if invitation is expired
  const isExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  // Helper to format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Define table columns
  const columns: AppTableColumn<UserInvitation>[] = useMemo(
    () => [
      {
        key: "firstName",
        label: "First Name",
        sortable: true,
        width: 180,
        minWidth: 180,
        render: (value) => <Text fw={500}>{value || "—"}</Text>,
      },
      {
        key: "lastName",
        label: "Last Name",
        sortable: true,
        width: 180,
        minWidth: 180,
        render: (value) => <Text fw={500}>{value || "—"}</Text>,
      },
      {
        key: "email",
        label: "Email",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value) => <Text>{value || "—"}</Text>,
      },
      {
        key: "userRole",
        label: "Role",
        sortable: true,
        width: 200,
        minWidth: 200,
        render: (_, record) => <Text>{record.userRole?.roleName || "—"}</Text>,
      },
      {
        key: "expiresAt",
        label: "Expires",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        hideOnMobile: true,
        render: (value, _) => {
          const expired = isExpired(value);
          return (
            <Text c={expired ? "red" : "dimmed"} fw={expired ? 500 : 400}>
              {formatDate(value)}
            </Text>
          );
        },
      },
      {
        key: "actions",
        label: "",
        width: 280,
        minWidth: 280,
        align: "left",
        render: (_, record) => (
          <Group gap={"sm"} justify="flex-end">
            <AppButton
              key="resend"
              variant="tertiary"
              iconName="Send"
              size="xs"
              onClick={() =>
                typeof onResendInvitation === "function" &&
                onResendInvitation(record)
              }
              tooltip="Resend invitation email"
              loading={resendingInvitationId === record.id}
              disabled={resendingInvitationId === record.id}
            />
            <RecordActions
              context={record}
              //onEditClick={onEditInvitation}
              onRemoveClick={onRemoveInvitation}
              removeInProgress={removingInvitationId === record.id}
              editTooltip="Edit invitation"
              removeTooltip="Delete invitation"
            />
          </Group>
        ),
      },
    ],
    [
      onEditInvitation,
      onRemoveInvitation,
      onResendInvitation,
      removingInvitationId,
      resendingInvitationId,
    ]
  );

  return (
    <AppTable
      data={userInvitations}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(invitation) => invitation?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
